import { LocationService } from "~/services/location/location.service";
import type { Address } from "~/types/address";

/* ------------------ HELPERS ------------------ */

const generateId = () => {
    return `loc_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
};

const isSameLocation = (gps1: any, gps2: any, tolerance = 0.0001) => {
    if (!gps1 || !gps2) return false;

    return (
        Math.abs(Number(gps1.latitude) - Number(gps2.latitude)) < tolerance &&
        Math.abs(Number(gps1.longitude) - Number(gps2.longitude)) < tolerance
    );
};

const parseNumber = (val: any): number | null => {
    const num = Number(val);
    return isNaN(num) ? null : num;
};

const extractCountry = (address: string | null | undefined): string => {
    // 1. Absolute fallback (handles null / undefined / empty)
    if (!address || typeof address !== "string") return "India";

    const normalized = address.trim();

    // 2. Check for (XX) format like (IN)
    const match = normalized.match(/\(([^)]+)\)/);
    if (match?.[1]) return match[1];

    // 3. Check explicit "India"
    if (normalized.toLowerCase().includes("india")) {
        return "India";
    }

    // 4. Detect Indian PIN code (strong signal)
    if (/\b\d{6}\b/.test(normalized)) {
        return "India";
    }

    // 5. Try last segment (common pattern: ..., Country)
    const parts = normalized.split(",");
    const lastPart = parts[parts.length - 1]?.trim();

    if (lastPart && !/^\d{5,6}$/.test(lastPart)) {
        return lastPart;
    }

    // 6. FINAL FALLBACK (never fail)
    return "India";
};

const cityCache = new Map<string, { name: string; code: string | null }>();

const resolveCity = async (data: any): Promise<{ code: string | null; name: string | null }> => {

    if (data?.city?.code) {
        return {
            code: data?.city?.code ?? null,
            name: data?.city?.name ?? null,
        };
    }

    const cityName = data?.city?.name || data?.city || null;

    if (!cityName) {
        return {
            code: null,
            name: null,
        };
    }

    if (cityCache.has(cityName)) {
        return cityCache.get(cityName)!;
    }

    try {
        const { data: cityData } = await LocationService.searchCities(cityName);

        const result = {
            name: cityName,
            code: cityData?.[0]?.code ?? null,
        };

        cityCache.set(cityName, result);
        return result;

    } catch {
        const fallback = {
            name: cityName,
            code: null,
        };

        cityCache.set(cityName, fallback);
        return fallback;
    }
};


export const mapGeoResponse = async (
    data: any,
    mapping: string
): Promise<Address> => {

    const id = data?.id || generateId();

    const city = await resolveCity(data);

    switch (mapping) {

        case 'FROM_COORDINATES':
        case 'FROM_ADDRESS':
            return {
                id,
                address_line1: data?.door ?? null,
                address_line2: data?.street ?? null,
                address_line3: data?.locality ?? null,
                area_code: data?.area_code ?? null,
                city,
                state: {
                    code: data?.state?.code ?? null,
                    name: data?.state ?? null,
                },
                country: {
                    code:
                        data?.country?.code ??
                        (extractCountry(data?.country)?.toLowerCase() === 'india'
                            ? "IN"
                            : null),
                    name: extractCountry(data?.country) ?? null,
                },
                gps: {
                    latitude: Number(data?.latitude) ?? null,
                    longitude: Number(data?.longitude) ?? null,
                },
                formatted_address: data?.formatted_address ?? null,
                contact: {
                    name: data?.contact?.name ?? null,
                    phone: data?.contact?.phone ?? null,
                    email: data?.contact?.email ?? null,
                },
                label: data?.label ?? null,
            };

        case 'DELIVERY_ADDRESS':
            return {
                id,
                address_line1: data?.door ?? null,
                address_line2: data?.street ?? null,
                address_line3: data?.locality ?? null,
                area_code: data?.area_code ?? null,
                city,
                state: {
                    code: data?.state?.code ?? null,
                    name: data?.state ?? null,
                },
                country: {
                    code:
                        data?.country?.code ??
                        (extractCountry(data?.country)?.toLowerCase() === 'india'
                            ? "IN"
                            : null),
                    name: extractCountry(data?.country) ?? null,
                },
                gps: {
                    latitude: Number(data?.latitude) ?? null,
                    longitude: Number(data?.longitude) ?? null,
                },
                formatted_address: data?.formatted_address ?? null,
                contact: {
                    name: data?.contact?.name ?? null,
                    phone: data?.contact?.phone ?? null,
                    email: data?.contact?.email ?? null,
                },
                label: data?.label ?? null,
            };

        case 'USER_ADDRESS':
            return {
                id,
                address_line1: data?.door ?? null,
                address_line2: data?.building ?? null,
                address_line3: data?.locality ?? null,
                area_code: data?.area_code ?? null,
                city,
                state: {
                    code: data?.state?.code ?? null,
                    name: data?.state ?? null,
                },
                country: {
                    code:
                        data?.country?.code ??
                        (extractCountry(data?.country)?.toLowerCase() === 'india'
                            ? "IN"
                            : null),
                    name: extractCountry(data?.country) ?? null,
                },
                gps: {
                    latitude: Number(data?.gps?.latitude) ?? null,
                    longitude: Number(data?.gps?.longitude) ?? null,
                },
                formatted_address: [
                    data?.door,
                    data?.street,
                    data?.locality,
                    data?.city,
                    data?.state,
                    extractCountry(data?.country)
                ].filter(Boolean).join(", ") || null,

                contact: {
                    name: data?.contact?.name ?? null,
                    phone: data?.contact?.phone ?? null,
                    email: data?.contact?.email ?? null,
                },
                label: data?.label ?? null,
            };

        default:
            return {
                id,
                address_line1: data?.address_line1 ?? null,
                address_line2: data?.address_line2 ?? null,
                address_line3: data?.address_line3 ?? null,
                area_code: data?.area_code ?? null,
                city,
                state: {
                    code: data?.state?.code ?? null,
                    name: data?.state?.name ?? null,
                },
                country: {
                    code:
                        data?.country?.code ??
                        (extractCountry(data?.country)?.toLowerCase() === 'india'
                            ? "IN"
                            : null),
                    name: extractCountry(data?.country) ?? null,
                },
                gps: {
                    latitude: Number(data?.gps?.latitude) ?? null,
                    longitude: Number(data?.gps?.longitude) ?? null,
                },
                formatted_address: data?.formatted_address ?? null,
                contact: {
                    name: data?.contact?.name ?? null,
                    phone: data?.contact?.phone ?? null,
                    email: data?.contact?.email ?? null,
                },
                label: data?.label ?? null,
            };
    }
};



export const mapDeliveryAddress = async (
    data: any,
): Promise<any> => {
    const addressStore = useUserAddressStore();

    let addressId = data?.id ?? null;

    // 🚀 If ID not available → fetch & match using GPS
    if (!addressId && data?.gps) {
        try {
            // Avoid repeated API calls
            if (!addressStore.addresses || addressStore.addresses.length === 0) {
                await addressStore.fetchAddresses();
            }

            const matched = addressStore.addresses?.find((addr: any) =>
                isSameLocation(addr?.gps, data?.gps)
            );

            if (matched) {
                addressId = matched.id;
            }
        } catch (err) {
            console.error("Address fetch/match failed:", err);
        }
    }

    return {
        address_id: addressId,
        name: data?.contact?.name ?? null,
        phone: data?.contact?.phone ?? null,
        email: data?.contact?.email ?? null,
        address_line1: data?.door ?? null,
        address_line2: data?.building ?? null,
        address_line3: data?.locality ?? null,
        area_code: data?.area_code ?? null,
        city: data?.city ?? null,
        state: data?.state ?? null,
        label: data?.label ?? null,
        gps: {
            latitude: parseNumber(data?.gps?.latitude),
            longitude: parseNumber(data?.gps?.longitude),
        },
    };
};